import React, { useEffect } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CBadge,
  CImage,
  CListGroup,
  CListGroupItem,
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionBody,
} from "@coreui/react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchProductDetails } from "../../features/seller/sellerSlice";
import AppLoader from "../../components/seller/AppLoader";
import { capitalizeFirst } from "../../utils/stringUtils";
import { formatCurrency, formatPrice } from "../../utils/price";
import { formatBidDateTime, timeAgo } from "../../utils/timeAgo";

export default function SellerProductDetailPage() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    detail: product,
    loading,
    error,
  } = useSelector((state) => state.seller.product);

  useEffect(() => {
    if (id) dispatch(fetchProductDetails(id));
  }, [id, dispatch]);

  if (loading) return <AppLoader />;
  if (error) return <p className="text-danger text-center">{error}</p>;
  if (!product) return null;

  const {
    name,
    listing_id,
    status,
    auction_status,
    allow_bidding,
    image_url,
    video_url,
    category,
    subcategory,
    location,
    quantity,
    unit_of_measurement,
    sorting_type,
    attachment,
    waste_range,
    hazard_types,
    hazard_description,
    description,
    specifications,
    weight,
    price,
    currency,
    minimum_asking_price,
    bid_increment,
    bid_closed_at,
    incoterms,
    lead_time,
    payment_terms,
    delivery_radius,
    delivery_port,
    faqs,
    seller,
  } = product;

  const isHazardous = hazard_types?.includes("hazardous");

  const showValue = (value, fallback = "N/A") => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return fallback;
    }
    return value;
  };

  const listingStatusColor =
    status === "approved"
      ? "success"
      : status === "pending"
        ? "warning"
        : "danger";

  const biddingStatusColor =
    auction_status === "open" ? "success" : "secondary";

  return (
    <>
      {/* ================= HEADER ================= */}
      <CRow className="mb-4">
        <CCol xs={12}>
          <CCard>
            <CCardBody className="d-flex gap-4 align-items-start">
              <CImage src={image_url} width={160} rounded />

              <div className="flex-grow-1">
                <h4 className="mb-2">{showValue(name)}</h4>

                <div className="text-muted mb-2">
                  Listing ID: {showValue(listing_id)}
                </div>

                <div className="d-flex flex-column gap-1">
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted ">Listing Status:</span>
                    <CBadge color={listingStatusColor}>
                      {capitalizeFirst(showValue(status))}
                    </CBadge>
                  </div>

                  {allow_bidding === 1 && (
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted ">Bidding Status:</span>
                      <CBadge color={biddingStatusColor}>
                        {capitalizeFirst(showValue(auction_status))}
                      </CBadge>
                    </div>
                  )}
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* ================= ROW 1: BASIC + TECH ================= */}
      <CRow className="g-4 mb-2">
        <CCol md={6} className="d-flex">
          <CCard className="flex-fill">
            <CCardHeader>Basic Details</CCardHeader>
            <CListGroup flush>
              <CListGroupItem>
                <strong>Category:</strong> {showValue(category?.name)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Subcategory:</strong> {showValue(subcategory?.name)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Metal Code:</strong>{" "}
                {showValue(subcategory?.metal_code)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Location:</strong> {showValue(location)}
              </CListGroupItem>
            </CListGroup>
          </CCard>
        </CCol>

        <CCol md={6} className="d-flex">
          <CCard className="flex-fill">
            <CCardHeader>Technical Specifications</CCardHeader>
            <CListGroup flush>
              <CListGroupItem>
                <strong>Quantity:</strong> {showValue(quantity)}{" "}
                {showValue(unit_of_measurement)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Sorting Type:</strong> {showValue(sorting_type)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Attachment:</strong> {showValue(attachment)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Waste % Range:</strong> {showValue(waste_range)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Hazard:</strong>{" "}
                {isHazardous ? "Hazardous" : "Non-Hazardous"}
              </CListGroupItem>
              {isHazardous && (
                <CListGroupItem>
                  <strong>Description:</strong> {showValue(hazard_description)}
                </CListGroupItem>
              )}
            </CListGroup>
          </CCard>
        </CCol>
      </CRow>

      {/* ================= PRODUCT INFO ================= */}
      <CRow className="mb-2">
        <CCol xs={12}>
          <CCard>
            <CCardHeader>Product Information</CCardHeader>
            <CListGroup flush>
              <CListGroupItem>
                <strong>Name:</strong> {showValue(name)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Description:</strong>
                <div
                  className="mt-2 ql-editor"
                  style={{ padding: 0 }}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </CListGroupItem>
            </CListGroup>
          </CCard>
        </CCol>
      </CRow>

      {/* ================= SPECIFICATIONS ================= */}
      {/* {specifications?.length > 0 && (
        <CRow className="mb-2">
          <CCol xs={12}>
            <CCard>
              <CCardHeader>Product Specifications</CCardHeader>
              <CListGroup flush>
                {specifications.map((spec) => (
                  <CListGroupItem key={spec.id}>
                    <strong>{spec.name}:</strong> {spec.value}
                  </CListGroupItem>
                ))}
              </CListGroup>
            </CCard>
          </CCol>
        </CRow>
      )} */}

      {/* ================= ROW 2: PRICING + BIDS ================= */}
      <CRow className="g-4 mb-2">
        <CCol md={6} className="d-flex">
          <CCard className="flex-fill">
            <CCardHeader>Pricing & Estimation</CCardHeader>
            <CListGroup flush>
              <CListGroupItem>
                <strong>Weight:</strong> {showValue(weight)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Price:</strong>  {showValue(formatCurrency(price))}
              </CListGroupItem>
            </CListGroup>
          </CCard>
        </CCol>

        {allow_bidding === 1 && (
          <CCol md={6} className="d-flex">
            <CCard className="flex-fill">
              <CCardHeader>Bids Information</CCardHeader>
              <CListGroup flush>
                <CListGroupItem>
                  <strong>Minimum Asking Price:</strong> {formatCurrency(minimum_asking_price)}
                </CListGroupItem>
                <CListGroupItem>
                  <strong>Increment:</strong> {formatCurrency(bid_increment)}
                </CListGroupItem>
                <CListGroupItem>
                  <strong>Closes At:</strong> {showValue(formatBidDateTime(bid_closed_at))}
                </CListGroupItem>
              </CListGroup>
            </CCard>
          </CCol>
        )}
      </CRow>

      {/* ================= ROW 3: SHIPPING + SELLER ================= */}
      <CRow className="g-4 mb-2">
        <CCol md={6} className="d-flex">
          <CCard className="flex-fill">
            <CCardHeader>Shipping & Trade Terms</CCardHeader>
            <CListGroup flush>
              <CListGroupItem>
                <strong>Incoterms:</strong> {showValue(incoterms)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Lead Time:</strong> {showValue(lead_time)} days
              </CListGroupItem>
              <CListGroupItem>
                <strong>Payment:</strong> {showValue(payment_terms)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Delivery Radius:</strong> {showValue(delivery_radius)}{" "}
                km
              </CListGroupItem>
              <CListGroupItem>
                <strong>Port:</strong> {showValue(delivery_port)}
              </CListGroupItem>
            </CListGroup>
          </CCard>
        </CCol>

        <CCol md={6} className="d-flex">
          <CCard className="flex-fill">
            <CCardHeader>Seller Information</CCardHeader>
            <CListGroup flush>
              <CListGroupItem>
                <strong>Name:</strong> {showValue(seller?.name)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Company:</strong> {showValue(seller?.company_name)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Email:</strong> {showValue(seller?.email)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>Phone:</strong> {showValue(seller?.phone)}
              </CListGroupItem>
              <CListGroupItem>
                <strong>KYC:</strong>{" "}
                <CBadge
                  color={
                    seller?.kyc_status === "verified" ? "success" : "warning"
                  }
                >
                  {showValue(seller?.kyc_status)}
                </CBadge>
              </CListGroupItem>
            </CListGroup>
          </CCard>
        </CCol>
      </CRow>

      {/* ================= MEDIA ================= */}
      <CRow className="mb-2">
        <CCol xs={12}>
          <CCard>
            <CCardHeader>Product Media</CCardHeader>
            <CCardBody>
              <div style={{ display: "flex", gap: "16px" }}>
                {/* IMAGE */}
                {image_url && (
                  <div style={mediaWrapperStyle}>
                    <CImage
                      src={image_url}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                )}

                {/* VIDEO */}
                {video_url && (
                  <div style={mediaWrapperStyle}>
                    <video
                      src={video_url}
                      controls
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                )}
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* ================= FAQs ================= */}
      {faqs?.length > 0 && (
        <CRow>
          <CCol xs={12}>
            <CCard>
              <CCardHeader>FAQs</CCardHeader>
              <CCardBody>
                <CAccordion>
                  {faqs.map((faq, i) => (
                    <CAccordionItem key={faq.id} itemKey={i}>
                      <CAccordionHeader>{faq.question}</CAccordionHeader>
                      <CAccordionBody>{faq.answer}</CAccordionBody>
                    </CAccordionItem>
                  ))}
                </CAccordion>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      )}
    </>
  );
}

const mediaWrapperStyle = {
  width: "100%",
  flex: 1, // 👈 key fix
  height: "280px",
  backgroundColor: "#f8f9fa",
  borderRadius: "8px",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
